import {
  useLicensingServiceV1,
  useUserManagementServiceV1,
} from "@rw1/api-services";
import { Licensing_V1, UserManagement_V1 } from "@rw1/auto-generated-clients";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CellContext, ColumnDef, SortingState } from "@tanstack/react-table";
import { FC, useCallback, useMemo, useState } from "react";
import { Trans } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { GrantAccessToGroupDialog } from "../../../components";
import { ConfirmationDialog } from "../../../components/ConfirmationDialog";
import { DataTable } from "../../../components/data-table/DataTable";
import { DeleteActionCell } from "../../../components/data-table/DeleteActionCell";
import { ALL_RELEASE_CHANNELS_QUERY_KEY } from "../../../hooks/useAllExperiences";
import { USER_GROUPS_QUERY_KEY } from "../../../hooks/useAllUserGroupsForACompany";
import {
  EXTERNAL_EXPERIENCES_QUERY_KEY,
  LICENSE_GROUP_ALLOCATIONS_QUERY_KEY,
  useExternalExperiences,
} from "../../../hooks/useExternalExperiences";
import { useLocalization } from "../../../hooks/useLocalization";
import { formatDateYMD } from "../../../shared/utils/date";
import { Routes } from "../../../types/NavigationPaths";
import { INCOMING_LICENSES_QUERY_KEY } from "../../experiences/hooks/useIncomingLicenses";
import { USERS_FOR_RELEASE_CHANNEL_QUERY_KEY } from "../../experiences/hooks/useUsersForReleaseChannel";
import { ALL_LICENSES_QUERY_KEY } from "../../licenses/hooks/useExternalLicenses";
import { USER_RELEASE_CHANNELS_QUERY_KEY } from "../../users/hooks/useUserExperiences";
import { USER_GROUP_QUERY_KEY } from "../hooks/useUserGroupById";
import {
  USER_GROUP_EXPERIENCES_QUERY_KEY,
  UserGroupExperienceRow,
} from "../hooks/useUserGroupExperiences";

interface ExperiencesTabProps {
  experiences: UserGroupExperienceRow[];
  userGroupName?: string;
  groupId?: string;
  isLoading?: boolean;
}

const INITIAL_SORTING: SortingState = [{ id: "title", desc: false }];

export const ExperiencesTab: FC<ExperiencesTabProps> = ({
  experiences,
  userGroupName,
  groupId,
  isLoading,
}) => {
  const { t } = useLocalization();
  const queryClient = useQueryClient();
  const userManagementClient = useUserManagementServiceV1();
  const licensingService = useLicensingServiceV1();
  const { releaseChannelIdToLicenseMap } = useExternalExperiences();
  const navigate = useNavigate();
  const { companyId } = useParams();

  const [isRemoveAccessDialogOpen, setIsRemoveAccessDialogOpen] =
    useState(false);
  const [selectedExperienceToRemove, setSelectedExperienceToRemove] =
    useState<UserGroupExperienceRow | null>(null);
  const [isGrantAccessDialogOpen, setIsGrantAccessDialogOpen] = useState(false);

  const handleOpenGrantAccessDialogOnClose = () => {
    setIsGrantAccessDialogOpen(false);
  };

  const handleGrantAccessOnClick = useCallback(() => {
    setIsGrantAccessDialogOpen(true);
  }, []);

  const handleOnRowClick = (experience: UserGroupExperienceRow) => {
    void navigate(`/companies/${companyId}${Routes.EXPERIENCES}/${experience.id}`);
  };

  const addReleaseChannelMutation = useMutation({
    mutationFn: ({
      releaseChannelId,
      expirationDate,
    }: {
      releaseChannelId: string;
      expirationDate?: Date;
    }) => {
      if (!groupId) {
        throw new Error("Group ID is required");
      }
      const releaseChannel = new UserManagement_V1.ReleaseChannel({
        releaseChannelId,
        requirements: UserManagement_V1.ReleaseChannelRequirements.Optional,
        userGroupId: groupId,
        expiresAt: expirationDate,
      });

      return userManagementClient.addReleaseChannelToUserGroup(
        groupId,
        releaseChannel,
      );
    },
    onSuccess: (_, variables) => {
      const newReleaseChannel = new UserManagement_V1.ReleaseChannel({
        releaseChannelId: variables.releaseChannelId,
        requirements: UserManagement_V1.ReleaseChannelRequirements.Optional,
        userGroupId: groupId,
        expiresAt: variables.expirationDate,
      });

      queryClient.setQueryData(
        [USER_GROUP_EXPERIENCES_QUERY_KEY, groupId],
        (old: UserManagement_V1.ReleaseChannel[] | undefined) => {
          return old ? [...old, newReleaseChannel] : [newReleaseChannel];
        },
      );
      void queryClient.invalidateQueries({
        queryKey: [USER_GROUPS_QUERY_KEY],
      });
      void queryClient.invalidateQueries({
        queryKey: [USER_GROUP_EXPERIENCES_QUERY_KEY, groupId],
      });
      void queryClient.invalidateQueries({
        queryKey: [USER_GROUP_QUERY_KEY, groupId],
      });
      void queryClient.invalidateQueries({
        queryKey: [ALL_RELEASE_CHANNELS_QUERY_KEY],
      });
      void queryClient.invalidateQueries({
        queryKey: [
          USERS_FOR_RELEASE_CHANNEL_QUERY_KEY,
          variables.releaseChannelId,
        ],
      });
    },
  });

  const allocateLicenseGroupSeatMutation = useMutation({
    mutationFn: ({
      licenseId,
      expirationDate,
    }: {
      licenseId: string;
      expirationDate?: Date;
    }) => {
      if (!groupId) {
        throw new Error("Group ID is required");
      }

      const allocation = new Licensing_V1.LicenseGroupAllocationWithoutId({
        userGroupId: groupId,
        expiresAt: expirationDate,
        isPinnedToLicenseExpiration: !expirationDate,
        isRevoked: false,
      });

      return licensingService.allocateLicenseGroupSeat(licenseId, allocation);
    },
    onSuccess: (_, variables) => {
      const licenseExpiration = queryClient
        .getQueryData<Licensing_V1.License[]>([EXTERNAL_EXPERIENCES_QUERY_KEY])
        ?.find((lic) => lic.licenseId === variables.licenseId)?.expiresAt;

      const newAllocation = new Licensing_V1.LicenseGroupAllocationWithoutId({
        userGroupId: groupId ?? "",
        expiresAt: variables.expirationDate ?? licenseExpiration,
        isPinnedToLicenseExpiration: !variables.expirationDate,
        isRevoked: false,
      });

      queryClient.setQueryData(
        [LICENSE_GROUP_ALLOCATIONS_QUERY_KEY, variables.licenseId],
        (old: Licensing_V1.LicenseGroupAllocation[] | undefined) => {
          return old ? [...old, newAllocation] : [newAllocation];
        },
      );
      void queryClient.invalidateQueries({
        queryKey: [LICENSE_GROUP_ALLOCATIONS_QUERY_KEY, variables.licenseId],
      });
      void queryClient.invalidateQueries({
        queryKey: [USER_GROUP_EXPERIENCES_QUERY_KEY, groupId],
      });
      void queryClient.invalidateQueries({
        queryKey: [USER_GROUP_QUERY_KEY, groupId],
      });
      void queryClient.invalidateQueries({
        queryKey: [USER_GROUPS_QUERY_KEY],
      });
      void queryClient.invalidateQueries({
        queryKey: [INCOMING_LICENSES_QUERY_KEY],
      });
      void queryClient.invalidateQueries({
        queryKey: [ALL_LICENSES_QUERY_KEY],
      });
    },
  });

  const removeAccessMutation = useMutation({
    mutationFn: ({
      userGroupId,
      releaseChannelId,
    }: {
      userGroupId: string;
      releaseChannelId: string;
    }) => {
      return userManagementClient.removeReleaseChannelFromUserGroup(
        userGroupId,
        releaseChannelId,
      );
    },
    onSuccess: (_, variables) => {
      queryClient.setQueryData(
        [USER_GROUP_EXPERIENCES_QUERY_KEY, groupId],
        (old: UserManagement_V1.ReleaseChannel[] | undefined) => {
          return (
            old?.filter(
              (rc) => rc.releaseChannelId !== variables.releaseChannelId,
            ) ?? []
          );
        },
      );
      void queryClient.invalidateQueries({
        queryKey: [USER_GROUP_EXPERIENCES_QUERY_KEY, groupId],
      });
      void queryClient.invalidateQueries({
        queryKey: [USER_RELEASE_CHANNELS_QUERY_KEY],
      });
      void queryClient.invalidateQueries({
        queryKey: [USER_GROUP_QUERY_KEY, groupId],
      });
      void queryClient.invalidateQueries({
        queryKey: [USER_GROUPS_QUERY_KEY],
      });
      void queryClient.invalidateQueries({
        queryKey: [ALL_RELEASE_CHANNELS_QUERY_KEY],
      });
      void queryClient.invalidateQueries({
        queryKey: [
          USERS_FOR_RELEASE_CHANNEL_QUERY_KEY,
          variables.releaseChannelId,
        ],
      });

      setIsRemoveAccessDialogOpen(false);
      setSelectedExperienceToRemove(null);
    },
  });

  const deallocateLicenseGroupSeatMutation = useMutation({
    mutationFn: ({ licenseId }: { licenseId: string }) => {
      if (!groupId) {
        throw new Error("Group ID is required");
      }

      return licensingService.deallocateLicenseGroupSeat(licenseId, groupId);
    },
    onSuccess: (_, variables) => {
      queryClient.setQueryData(
        [LICENSE_GROUP_ALLOCATIONS_QUERY_KEY, variables.licenseId],
        (old: Licensing_V1.LicenseGroupAllocation[] | undefined) => {
          return (
            old?.filter((allocation) => allocation.userGroupId !== groupId) ??
            []
          );
        },
      );
      void queryClient.invalidateQueries({
        queryKey: [LICENSE_GROUP_ALLOCATIONS_QUERY_KEY, variables.licenseId],
      });
      void queryClient.invalidateQueries({
        queryKey: [USER_GROUP_EXPERIENCES_QUERY_KEY, groupId],
      });
      void queryClient.invalidateQueries({
        queryKey: [USER_RELEASE_CHANNELS_QUERY_KEY],
      });
      void queryClient.invalidateQueries({
        queryKey: [USER_GROUP_QUERY_KEY, groupId],
      });
      void queryClient.invalidateQueries({
        queryKey: [USER_GROUPS_QUERY_KEY],
      });
      void queryClient.invalidateQueries({
        queryKey: [INCOMING_LICENSES_QUERY_KEY],
      });
      void queryClient.invalidateQueries({
        queryKey: [ALL_LICENSES_QUERY_KEY],
      });

      setIsRemoveAccessDialogOpen(false);
      setSelectedExperienceToRemove(null);
    },
  });

  const handleGrantAllocation = async ({
    releaseChannelId,
    expirationDate,
  }: {
    releaseChannelId: string;
    expirationDate?: Date;
  }) => {
    const license = releaseChannelIdToLicenseMap.get(releaseChannelId);

    if (license) {
      await allocateLicenseGroupSeatMutation.mutateAsync({
        licenseId: license.licenseId,
        expirationDate,
      });
    }
  };

  const handleRemoveAccess = useCallback(
    (selectedUserGroup: UserGroupExperienceRow) => {
      setSelectedExperienceToRemove(selectedUserGroup);
      setIsRemoveAccessDialogOpen(true);
    },
    [],
  );

  const handleRemoveAccessCancel = () => {
    setIsRemoveAccessDialogOpen(false);
    setSelectedExperienceToRemove(null);
    removeAccessMutation.reset();
    deallocateLicenseGroupSeatMutation.reset();
  };

  const handleRemoveAccessConfirm = () => {
    if (selectedExperienceToRemove && groupId) {
      const isExternalExperience = releaseChannelIdToLicenseMap.has(
        selectedExperienceToRemove.id,
      );

      if (isExternalExperience) {
        const license = releaseChannelIdToLicenseMap.get(
          selectedExperienceToRemove.id,
        );
        if (license) {
          deallocateLicenseGroupSeatMutation.mutate({
            licenseId: license.licenseId,
          });
        }
      } else {
        removeAccessMutation.mutate({
          userGroupId: groupId,
          releaseChannelId: selectedExperienceToRemove.id,
        });
      }
    }
  };

  const renderActionCell = (ctx: CellContext<UserGroupExperienceRow, any>) => {
    const handleClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      handleRemoveAccess(ctx.row.original);
    };

    return (
      <DeleteActionCell
        actionText={t("groups.removeAccess")}
        onClick={handleClick}
      />
    );
  };

  const hasAccessContentMetadataIds = useMemo(() => {
    return experiences.map((experience) => experience.contentMetadataId);
  }, [experiences]);

  const columns = useMemo<ColumnDef<UserGroupExperienceRow, any>[]>(
    () => [
      {
        id: "title",
        header: t("groups.experiencesTableHeaders.title"),
        accessorKey: "title",
        enableSorting: true,
        enableColumnFilter: false,
        sortingFn: "text",
        size: 130,
      },
      {
        accessorKey: "companyName",
        header: t("groups.experiencesTableHeaders.company"),
        enableSorting: false,
        enableColumnFilter: true,
        filterFn: "arrIncludesSome",
        size: 80,
      },
      {
        accessorKey: "expirationDate",
        header: t("groups.experiencesTableHeaders.expirationDate"),
        cell: renderExpirationDateCell,
        enableSorting: true,
        enableColumnFilter: false,
        size: 90,
      },
      {
        id: "actions",
        header: undefined,
        cell: renderActionCell,
        size: 70,
        enableSorting: false,
        enableColumnFilter: false,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t],
  );

  const isRemovingAccess = useMemo(
    () =>
      removeAccessMutation.isPending ||
      deallocateLicenseGroupSeatMutation.isPending,
    [
      removeAccessMutation.isPending,
      deallocateLicenseGroupSeatMutation.isPending,
    ],
  );

  const isSubmitLoading = useMemo(
    () =>
      addReleaseChannelMutation.isPending ||
      allocateLicenseGroupSeatMutation.isPending,
    [
      addReleaseChannelMutation.isPending,
      allocateLicenseGroupSeatMutation.isPending,
    ],
  );

  return (
    <>
      <DataTable<UserGroupExperienceRow>
        actionButtonText={t("groups.grantAccess")}
        actionButtonTextShort={t("groups.grant")}
        columns={columns}
        data={experiences}
        defaultSort={INITIAL_SORTING}
        facetedColumnIds={["companyName"]}
        globalFilterPlaceholder={t("groups.searchExperiences")}
        isLoading={isLoading}
        onActionClick={handleGrantAccessOnClick}
        onRowClick={handleOnRowClick}
      />
      <GrantAccessToGroupDialog
        hasAccessContentMetadataIds={hasAccessContentMetadataIds}
        hasAccessText={t("groups.hasAccess")}
        isOpen={isGrantAccessDialogOpen}
        isSubmitLoading={isSubmitLoading}
        onClose={handleOpenGrantAccessDialogOnClose}
        onGrantAccess={addReleaseChannelMutation.mutateAsync}
        onGrantAllocation={handleGrantAllocation}
      />
      <ConfirmationDialog.Root
        aria-describedby="remove-access-from-group-dialog-description"
        aria-labelledby="remove-access-from-group-dialog-title"
        maxWidth="xs"
        onClose={handleRemoveAccessCancel}
        open={isRemoveAccessDialogOpen}
      >
        <ConfirmationDialog.Title
          id="remove-from-group-dialog-title"
          showIcon={true}
        >
          {t("groups.removeAccess")}
        </ConfirmationDialog.Title>
        <ConfirmationDialog.Content>
          <ConfirmationDialog.Text id="remove-access-from-group-dialog-description">
            <Trans
              components={{ strong: <strong /> }}
              i18nKey="groups.removeAccessConfirmation"
              values={{
                experienceTitle: selectedExperienceToRemove?.title,
                groupName: userGroupName,
              }}
            />
          </ConfirmationDialog.Text>
        </ConfirmationDialog.Content>
        <ConfirmationDialog.Actions>
          <ConfirmationDialog.Button
            onClick={handleRemoveAccessCancel}
            variant="contained"
          >
            {t("groups.cancel")}
          </ConfirmationDialog.Button>
          <ConfirmationDialog.Button
            isLoading={isRemovingAccess}
            loadingText={t("groups.removing")}
            onClick={handleRemoveAccessConfirm}
            variant="secondary"
          >
            {t("groups.remove")}
          </ConfirmationDialog.Button>
        </ConfirmationDialog.Actions>
      </ConfirmationDialog.Root>
    </>
  );
};

const renderExpirationDateCell = (
  ctx: CellContext<UserGroupExperienceRow, any>,
) => {
  const expirationDate = ctx.getValue<Date | undefined>();

  return formatDateYMD(expirationDate);
};
